import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Trash2, Lock, Unlock, ExternalLink } from "lucide-react";
import { api, errMsg, useFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, ErrorState, EmptyState, StatusBadge, Field, Confirm, OfficialBadge, BackLink } from "@/components/common";
import { StandingsTable } from "@/components/StandingsTable";
import { DashHeader } from "@/dashboard/DashboardLayout";
import { EventFormDialog } from "@/dashboard/AdminEvents";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EVENT_STATUS, fmtDateTime } from "@/lib/format";

const tabCls = "data-[state=active]:bg-[#17171D] data-[state=active]:text-[#F5F5F7] text-[#C6C6CE]/70 font-display uppercase tracking-wider text-xs";

function Registrations({ ev, regs, onChange }) {
  const clans = useFetch("/clans", { limit: 100 });
  const [f, setF] = useState({ clan_id: "", name: "", players: "" });
  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/events/${ev.id}/registrations`, { clan_id: f.clan_id || null, name: f.name || null, players: f.players.split(",").map((x) => x.trim()).filter(Boolean) });
      setF({ clan_id: "", name: "", players: "" });
      toast.success("Partecipante iscritto");
      onChange();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const del = async (id) => {
    try { await api.delete(`/admin/registrations/${id}`); toast.success("Iscrizione rimossa"); onChange(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <form onSubmit={add} className="nx-card p-5 space-y-3 self-start" data-testid="registration-form">
        <div className="eyebrow">Iscrivi partecipante</div>
        <Field label="Clan (opzionale)"><select data-testid="registration-clan-select" className="nx-input" value={f.clan_id} onChange={(e) => setF({ ...f, clan_id: e.target.value })}><option value="">— Nessun clan —</option>{(clans.data?.items || []).map((c) => <option key={c.id} value={c.id}>{c.name} [{c.tag}]</option>)}</select></Field>
        <Field label="Nome squadra / giocatore"><input data-testid="registration-name-input" placeholder="Default: nome del clan" className="nx-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <Field label="Giocatori (separati da virgola)"><input data-testid="registration-players-input" className="nx-input" value={f.players} onChange={(e) => setF({ ...f, players: e.target.value })} /></Field>
        <button data-testid="registration-submit" disabled={ev.results_locked} className="btn-primary btn-sm w-full">Iscrivi</button>
      </form>
      <div className="lg:col-span-2">
        {regs.length ? (
          <div className="nx-card divide-y divide-[#C6C6CE]/5" data-testid="registrations-list">
            {regs.map((r) => (
              <div key={r.id} className="p-4 flex items-center gap-3">
                {r.tag && <span className="font-num text-[10px] px-1.5 py-0.5 border border-[#C6C6CE]/20 rounded">{r.tag}</span>}
                <div className="flex-1 min-w-0"><div className="text-[#F5F5F7]">{r.name}</div>{r.players?.length > 0 && <div className="text-xs text-[#C6C6CE]/50 truncate">{r.players.join(", ")}</div>}</div>
                {!ev.results_locked && <Confirm testId={`del-reg-${r.id}`} trigger={<button aria-label="Rimuovi" data-testid={`delete-registration-${r.id}`} className="btn-danger"><Trash2 className="w-3.5 h-3.5" /></button>} title="Rimuovere l'iscrizione?" description={`${r.name} verrà rimosso dall'evento.`} onConfirm={() => del(r.id)} />}
              </div>
            ))}
          </div>
        ) : <EmptyState title="Nessun partecipante iscritto" />}
      </div>
    </div>
  );
}

const emptyResult = { registration_id: "", match_label: "", day: "", round: "", phase: "", placement: "", kills: 0, wins: 0, bonus: 0, extra: {}, notes: "" };

function ResultForm({ ev, regs, editing, onDone }) {
  const [f, setF] = useState(editing ? { ...emptyResult, ...editing, day: editing.day || "", round: editing.round || "", placement: editing.placement || "" } : emptyResult);
  const extra = ev.score_rules?.extra_stats || [];
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    const n = (v) => (v === "" || v == null ? null : Number(v));
    const payload = { registration_id: f.registration_id, match_label: f.match_label, day: n(f.day), round: n(f.round), phase: f.phase, placement: n(f.placement), kills: Number(f.kills || 0), wins: Number(f.wins || 0), bonus: Number(f.bonus || 0), extra: Object.fromEntries(Object.entries(f.extra || {}).map(([k, v]) => [k, Number(v || 0)])), notes: f.notes };
    try {
      if (editing) await api.put(`/admin/results/${editing.id}`, payload);
      else await api.post(`/admin/events/${ev.id}/results`, payload);
      toast.success(editing ? "Risultato aggiornato" : "Risultato registrato · classifica aggiornata");
      setF({ ...emptyResult, registration_id: "", day: f.day, round: f.round, phase: f.phase, match_label: f.match_label });
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <form onSubmit={submit} className="nx-card p-5 space-y-3" data-testid="result-form">
      <div className="eyebrow">{editing ? "Modifica risultato" : "Registra risultato"}</div>
      <Field label="Partecipante"><select data-testid="result-registration-select" required className="nx-input" value={f.registration_id} onChange={set("registration_id")}><option value="">Seleziona</option>{regs.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Match"><input data-testid="result-match-input" className="nx-input" value={f.match_label} onChange={set("match_label")} /></Field>
        <Field label="Fase"><input data-testid="result-phase-input" className="nx-input" value={f.phase} onChange={set("phase")} /></Field>
        <Field label="Giornata"><input data-testid="result-day-input" type="number" min={1} className="nx-input" value={f.day} onChange={set("day")} /></Field>
        <Field label="Turno"><input data-testid="result-round-input" type="number" min={1} className="nx-input" value={f.round} onChange={set("round")} /></Field>
        <Field label="Piazzamento"><input data-testid="result-placement-input" type="number" min={1} className="nx-input" value={f.placement} onChange={set("placement")} /></Field>
        <Field label="Eliminazioni"><input data-testid="result-kills-input" type="number" min={0} className="nx-input" value={f.kills} onChange={set("kills")} /></Field>
        <Field label="Vittorie"><input data-testid="result-wins-input" type="number" min={0} className="nx-input" value={f.wins} onChange={set("wins")} /></Field>
        <Field label="Bonus / penalità"><input data-testid="result-bonus-input" type="number" step="any" className="nx-input" value={f.bonus} onChange={set("bonus")} /></Field>
        {extra.map((s) => <Field key={s.key} label={s.label}><input data-testid={`result-extra-${s.key}`} type="number" step="any" className="nx-input" value={f.extra?.[s.key] ?? ""} onChange={(e) => setF({ ...f, extra: { ...f.extra, [s.key]: e.target.value } })} /></Field>)}
      </div>
      <Field label="Note"><input data-testid="result-notes-input" className="nx-input" value={f.notes} onChange={set("notes")} /></Field>
      <div className="flex gap-2"><button data-testid="result-submit" className="btn-primary btn-sm flex-1">{editing ? "Aggiorna" : "Registra"}</button>{editing && <button type="button" onClick={() => onDone(true)} className="btn-ghost btn-sm">Annulla</button>}</div>
    </form>
  );
}

function Results({ ev, regs, results, onChange }) {
  const [editing, setEditing] = useState(null);
  const del = async (id) => {
    try { await api.delete(`/admin/results/${id}`); toast.success("Risultato eliminato"); onChange(); } catch (err) { toast.error(errMsg(err)); }
  };
  if (ev.results_locked) {
    return <EmptyState icon={Lock} title="Risultati bloccati" text="La classifica ufficiale è pubblicata. Per correggere i risultati il Super Admin deve riaprire l'evento con una motivazione (scheda Pubblicazione)." testId="results-locked" />;
  }
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="self-start"><ResultForm key={editing?.id || "new"} ev={ev} regs={regs} editing={editing} onDone={(cancel) => { setEditing(null); if (!cancel) onChange(); }} /></div>
      <div className="lg:col-span-2">
        {results.length ? (
          <div className="nx-card overflow-x-auto" data-testid="results-list">
            <table className="w-full text-sm min-w-[640px]">
              <thead><tr className="text-[10px] uppercase tracking-widest text-[#C6C6CE]/50 text-left border-b border-[#C6C6CE]/10"><th className="p-3">Partecipante</th><th>Match</th><th>G/T/Fase</th><th>Pos</th><th>E</th><th>V</th><th>Punti</th><th /></tr></thead>
              <tbody>
                {results.map((r) => (
                  <tr key={r.id} className="border-b border-[#C6C6CE]/5" data-testid={`result-row-${r.id}`}>
                    <td className="p-3 text-[#F5F5F7]">{r.registration_name}</td><td className="text-xs">{r.match_label || "—"}</td>
                    <td className="text-xs font-num">{r.day || "-"}/{r.round || "-"}/{r.phase || "-"}</td><td className="font-num">{r.placement || "—"}</td>
                    <td className="font-num">{r.kills}</td><td className="font-num">{r.wins}</td><td className="font-num font-bold text-[#F5F5F7]">{r.points}</td>
                    <td className="pr-3 text-right whitespace-nowrap">
                      <button aria-label="Modifica" data-testid={`edit-result-${r.id}`} onClick={() => setEditing(r)} className="btn-ghost btn-sm px-2 mr-1"><Pencil className="w-3.5 h-3.5" /></button>
                      <Confirm testId={`del-result-${r.id}`} trigger={<button aria-label="Elimina" data-testid={`delete-result-${r.id}`} className="btn-danger px-2"><Trash2 className="w-3.5 h-3.5" /></button>} title="Eliminare il risultato?" description="La classifica live verrà ricalcolata. L'operazione è registrata nel registro attività." onConfirm={() => del(r.id)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <EmptyState title="Nessun risultato registrato" />}
      </div>
    </div>
  );
}

function Publish({ ev, standings, finals, onChange }) {
  const { user } = useAuth();
  const [awards, setAwards] = useState("");
  const [notes, setNotes] = useState("");
  const [reason, setReason] = useState("");
  const publish = async () => {
    const list = awards.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => { const [p, ...rest] = l.split("|"); return { position: Number(p), prize: rest.join("|").trim() }; });
    try {
      await api.post(`/admin/events/${ev.id}/finalize`, { awards: list, notes, confirm: true });
      toast.success("Classifica ufficiale pubblicata e archiviata");
      onChange();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const reopen = async () => {
    try {
      await api.post(`/admin/events/${ev.id}/reopen`, { reason });
      toast.success("Evento riaperto per correzione");
      setReason("");
      onChange();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <div className="space-y-6">
      {!ev.results_locked ? (
        <div className="nx-card p-5 space-y-4" data-testid="publish-panel">
          <div className="flex items-center gap-3"><OfficialBadge official={false} /><span className="text-sm text-[#C6C6CE]/70">Verifica la classifica provvisoria prima della pubblicazione definitiva.</span></div>
          <StandingsTable rows={standings} extraStats={ev.score_rules?.extra_stats || []} showChange={false} compact />
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Premi assegnati (uno per riga: posizione|premio)"><textarea data-testid="publish-awards-input" rows={3} className="nx-input font-num text-xs" placeholder="1|Trofeo + 100€" value={awards} onChange={(e) => setAwards(e.target.value)} /></Field>
            <Field label="Note di pubblicazione"><textarea data-testid="publish-notes-input" rows={3} className="nx-input" value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
          </div>
          <Confirm testId="publish-final" trigger={<button data-testid="publish-final-button" disabled={!standings.length} className="btn-primary"><Lock className="w-4 h-4" /> Pubblica classifica ufficiale</button>} title="Pubblicare la classifica ufficiale?" description="Verrà creata una versione ufficiale archiviata e non modificabile tramite aggiornamenti live. L'evento passerà allo stato Concluso." confirmLabel="Pubblica" onConfirm={publish} />
        </div>
      ) : (
        <div className="nx-card p-5 space-y-3" data-testid="locked-panel">
          <div className="flex items-center gap-3"><OfficialBadge official /><span className="text-sm text-[#C6C6CE]/70">Classifica ufficiale pubblicata. Risultati bloccati.</span></div>
          {user.role === "super_admin" ? (
            <div className="space-y-3">
              <Field label="Motivazione della correzione (obbligatoria, min. 5 caratteri)"><textarea data-testid="reopen-reason-input" rows={2} className="nx-input" value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
              <Confirm testId="reopen" trigger={<button data-testid="reopen-button" disabled={reason.trim().length < 5} className="btn-ghost btn-sm"><Unlock className="w-3.5 h-3.5" /> Riapri per correzione</button>} title="Riaprire i risultati?" description="L'azione viene registrata nello storico. La versione ufficiale attuale resta valida finché non ne pubblichi una nuova." onConfirm={reopen} />
            </div>
          ) : <p className="text-xs text-[#C6C6CE]/50">Solo il Super Admin può riaprire una classifica ufficiale.</p>}
        </div>
      )}
      <div className="nx-card p-5" data-testid="finals-versions"><div className="eyebrow mb-3">Versioni ufficiali</div>
        {finals.length ? <ul className="space-y-2 text-sm">{finals.map((f) => <li key={f.id} className="flex flex-wrap justify-between gap-2 border-b border-[#C6C6CE]/5 pb-2"><span className="text-[#F5F5F7]">v{f.version} · {f.status === "official" ? "ufficiale" : "sostituita"} · {f.published_by_name}</span><span className="font-num text-xs text-[#C6C6CE]/50">{fmtDateTime(f.published_at)}</span>{f.correction_reason && <span className="w-full text-xs text-amber-200">Correzione: {f.correction_reason}</span>}</li>)}</ul> : <p className="text-sm text-[#C6C6CE]/50">Nessuna versione pubblicata.</p>}
      </div>
    </div>
  );
}

export default function EventManage() {
  const { id } = useParams();
  const { data, loading, error, reload } = useFetch(`/admin/events/${id}`);
  const [edit, setEdit] = useState(false);
  if (loading && !data) return <Loader />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const { event: ev, registrations, results, standings, finals } = data;
  const setStatus = async (status) => {
    try { await api.post(`/admin/events/${ev.id}/status`, { status }); toast.success(`Stato: ${EVENT_STATUS[status]}`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="event-manage">
      <BackLink to="/dashboard/eventi" label="Eventi" />
      <div className="mt-4"><DashHeader title={ev.title} subtitle={`${registrations.length} partecipanti · ${results.length} risultati`}>
        <StatusBadge status={ev.status} />
        <select data-testid="event-status-select" aria-label="Cambia stato" className="nx-input py-1.5 text-xs w-auto" value={ev.status} onChange={(e) => setStatus(e.target.value)}>{Object.entries(EVENT_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
        <button data-testid="edit-event-button" onClick={() => setEdit(true)} className="btn-ghost btn-sm"><Pencil className="w-3.5 h-3.5" /> Modifica</button>
        {ev.status !== "bozza" && <Link to={`/eventi/${ev.slug}`} target="_blank" className="btn-ghost btn-sm"><ExternalLink className="w-3.5 h-3.5" /> Pagina pubblica</Link>}
      </DashHeader></div>
      <Tabs defaultValue="results">
        <TabsList className="bg-[#0B0B0F] border border-[#C6C6CE]/10 h-auto flex-wrap justify-start mb-6">
          <TabsTrigger value="regs" data-testid="manage-tab-registrations" className={tabCls}>Partecipanti</TabsTrigger>
          <TabsTrigger value="results" data-testid="manage-tab-results" className={tabCls}>Risultati</TabsTrigger>
          <TabsTrigger value="live" data-testid="manage-tab-live" className={tabCls}>Classifica live</TabsTrigger>
          <TabsTrigger value="publish" data-testid="manage-tab-publish" className={tabCls}>Pubblicazione</TabsTrigger>
        </TabsList>
        <TabsContent value="regs"><Registrations ev={ev} regs={registrations} onChange={reload} /></TabsContent>
        <TabsContent value="results"><Results ev={ev} regs={registrations} results={results} onChange={reload} /></TabsContent>
        <TabsContent value="live">{standings.length ? <StandingsTable rows={standings} extraStats={ev.score_rules?.extra_stats || []} /> : <EmptyState title="Classifica vuota" />}<p className="text-xs text-[#C6C6CE]/50 mt-3">Ultimo aggiornamento: {fmtDateTime(ev.standings_updated_at)}</p></TabsContent>
        <TabsContent value="publish"><Publish ev={ev} standings={standings} finals={finals} onChange={reload} /></TabsContent>
      </Tabs>
      {edit && <EventFormDialog open={edit} onOpenChange={setEdit} event={ev} onSaved={reload} />}
    </div>
  );
}
